import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipProvider as UITooltipProvider,
  TooltipTrigger as UITooltipTrigger
} from '@/shared/components/ui/tooltip';
import { toast } from 'sonner';
import GoogleMap from '@/shared/components/maps/GoogleMap';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell } from
'recharts';
import {
  Plus,
  Target,
  Globe,
  Users,
  TrendingUp,
  Calendar,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Link2,
  Copy,
  ExternalLink,
  MousePointer,
  Eye,
  FileText,
  Video,
  Mail,
  Share2,
  BarChart3,
  Clock,
  Zap,
  Code,
  Settings,
  Play,
  Pause,
  Map as MapIcon,
  MapPin,
  Key,
  Info,
  AlertTriangle,
  Loader2,
  Settings2,
  Save,
  CheckCircle2,
  XOctagon,
  Ticket,
  Ghost,
  Building2 } from
'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
};

const ASSET_ICONS = {
  landing_page: Globe,
  email: Mail,
  pdf: FileText,
  video: Video,
  social_post: Share2,
  ad: Target
};

const CHART_COLORS = ['#14b8a6', '#3b82f6', '#f97316', '#8b5cf6', '#22c55e', '#ef4444'];
const CAMPAIGN_TABS = new Set(['campaigns', 'analytics', 'links', 'tracking', 'map']);

// API Key Configuration Dialog
const ApiKeyDialog = ({ open, onOpenChange, onSave, currentKey }) => {
  const { t } = useTranslation("crm");
  const [apiKey, setApiKey] = useState(currentKey || '');

  useEffect(() => {
    if (open) {
      setApiKey(currentKey || localStorage.getItem('GOOGLE_MAPS_API_KEY') || localStorage.getItem('google_maps_api_key') || '');
    }
  }, [open, currentKey]);

  const handleSave = () => {
    if (apiKey.trim()) {
      onSave(apiKey.trim());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />{t("configureGoogleMaps", "Configure Google Maps API Key")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("apiKeyRequired", "API Key Required")}</AlertTitle>
            <AlertDescription className="text-sm">
              {t("toUseTheMap", "To use the map, you need a Google Maps API Key.")}
              <a
                href="https://console.cloud.google.com/google/maps-apis/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline ml-1">{t("getYourApi", "Get your API key here")}
              </a>
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("googleMapsApi", "Google Maps API Key")}</label>
            <Input
              type="password"
              placeholder="AIzaSy..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel", "Cancel")}</Button>
          <Button onClick={handleSave} disabled={!apiKey.trim()}>{t("saveApiKey", "Save API Key")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CampaignIntelligence = () => {const { t } = useTranslation("crm");
  const [searchParams] = useSearchParams();
  const requestedCampaignId = searchParams.get('campaign_id');
  const requestedTab = CAMPAIGN_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : null;
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [trackingLinks, setTrackingLinks] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [showNewLinkDialog, setShowNewLinkDialog] = useState(false);
  const [showTrackingScriptDialog, setShowTrackingScriptDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [trackingScript, setTrackingScript] = useState('');
  
  const [mapMarkers, setMapMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [googleApiKey, setGoogleApiKey] = useState(() => localStorage.getItem('google_maps_api_key') || localStorage.getItem('GOOGLE_MAPS_API_KEY') || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '');
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [selectedCampaignView, setSelectedCampaignView] = useState(null);
  
  const mapLayerOptions = [
    { id: 'members', label: 'Members', color: '#2563eb' },
    { id: 'clients', label: 'Our Partners', color: '#16a34a' },
    { id: 'bakery', label: 'Bakeries', color: '#f59e0b' },
    { id: 'grocery_or_supermarket', label: 'Groceries', color: '#0ea5e9' },
    { id: 'convenience_store', label: 'Convenience Stores', color: '#8b5cf6' },
    { id: 'gas_station', label: 'Gas Stations', color: '#ef4444' }
  ];
  const [selectedMapLayers, setSelectedMapLayers] = useState(['members', 'clients']);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  
  // Custom Dashboard State
  const defaultManualData = {
    kpis: { total: 0, ativos: 0, verificados: 0, bounced: 0, novos7dias: 0, novosEsteMes: 0 },
    engajamento: { comCupons: 0, emEventos: 0, semEngajamento: 0, multiEmpresa: 0 },
    linhaCupons: { pessoasUsaram: 0, claims: 0, resgatados: 0, claimSemResgate: 0, tempoMedio: 0, taxaResgate: 0 },
    dadosCompletos: { comTelefone: 0, comPostalCode: 0, completos: 0 },
    finances: { budget: 0, costs: 0, b2b: 0, b2c: 0 },
    regionData: [
      {"name": "M6N · Toronto", "value": 30},
      {"name": "M6E · Toronto", "value": 24},
      {"name": "M6H · Toronto", "value": 21},
      {"name": "M6K · Toronto", "value": 17},
      {"name": "M6M · Toronto", "value": 17}
    ],
    companyData: [
      {"name": "John's Burger", "value": 955},
      {"name": "Samba Eatery", "value": 56},
      {"name": "Stop BBQ", "value": 15}
    ]
  };
  const [manualData, setManualData] = useState(defaultManualData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSourceRefreshing, setIsSourceRefreshing] = useState(false);

  const handleOpenEdit = () => {
    setEditData(JSON.parse(JSON.stringify(manualData)));
    setIsModalOpen(true);
  };

  const handleSaveData = async () => {
    if (!selectedCampaignView) return;
    setIsSaving(true);
    try {
      const response = await fetch(`/api/campaign-intelligence/campaigns/${selectedCampaignView.id}/manual-data`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        setManualData(editData);
        setIsModalOpen(false);
        toast.success('Campaign data updated');
      } else {
        toast.error('Failed to update campaign data');
      }
    } catch (error) {
      console.error('Failed to save dashboard metrics:', error);
      toast.error('Error saving data');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveApiKey = async (key) => {
    setGoogleApiKey(key);
    localStorage.setItem('GOOGLE_MAPS_API_KEY', key);
    localStorage.setItem('google_maps_api_key', key);
    
    try {
      await fetch(`${API_URL}/api/api-keys/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          key_name: 'google_maps_api_key',
          value: key
        })
      });
    } catch (error) {
      console.error('Failed to save API key to backend:', error);
    }
    
    toast.success('API Key Saved', { description: 'Google Maps will now load' });
  };

  // New campaign form
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    target_modules: [],
    target_industries: [],
    tracking_domain: '',
    enable_website_tracking: true,
    enable_email_tracking: true,
    enable_social_tracking: true,
    start_date: '',
    end_date: '',
    budget: ''
  });

  // New tracking link form
  const [newLink, setNewLink] = useState({
    campaign_id: '',
    destination_url: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: ''
  });

  useEffect(() => {
    fetchData();
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      const res = await fetch(`${API_URL}/api/api-keys/google_maps_api_key?include_value=true`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        if (data.has_value && data.value) {
          setGoogleApiKey(data.value);
          localStorage.setItem('GOOGLE_MAPS_API_KEY', data.value);
          localStorage.setItem('google_maps_api_key', data.value);
        }
      }
    } catch (error) {
      console.error('Failed to fetch API key from backend:', error);
    }
  };

  const regionCoords = {
    "M6N · Toronto": { lat: 43.6736, lng: -79.4880 },
    "M6E · Toronto": { lat: 43.6888, lng: -79.4503 },
    "M6H · Toronto": { lat: 43.6633, lng: -79.4357 },
    "M6K · Toronto": { lat: 43.6372, lng: -79.4300 },
    "M6M · Toronto": { lat: 43.6934, lng: -79.4820 }
  };

  const fsaCoords = {
    M1B: { lat: 43.8067, lng: -79.1944 }, M1C: { lat: 43.7845, lng: -79.1605 },
    M1E: { lat: 43.7636, lng: -79.1887 }, M1G: { lat: 43.7709, lng: -79.2169 },
    M1H: { lat: 43.7731, lng: -79.2395 }, M1J: { lat: 43.7447, lng: -79.2395 },
    M1K: { lat: 43.7279, lng: -79.2620 }, M1L: { lat: 43.7111, lng: -79.2846 },
    M1M: { lat: 43.7163, lng: -79.2395 }, M1N: { lat: 43.6927, lng: -79.2648 },
    M1P: { lat: 43.7574, lng: -79.2733 }, M1R: { lat: 43.7501, lng: -79.2958 },
    M1S: { lat: 43.7942, lng: -79.2620 }, M1T: { lat: 43.7816, lng: -79.3043 },
    M1V: { lat: 43.8153, lng: -79.2846 }, M1W: { lat: 43.7995, lng: -79.3184 },
    M2H: { lat: 43.8038, lng: -79.3635 }, M2J: { lat: 43.7785, lng: -79.3466 },
    M2K: { lat: 43.7869, lng: -79.3859 }, M2L: { lat: 43.7575, lng: -79.3747 },
    M2M: { lat: 43.7891, lng: -79.4085 }, M2N: { lat: 43.7701, lng: -79.4085 },
    M2P: { lat: 43.7528, lng: -79.4000 }, M2R: { lat: 43.7827, lng: -79.4423 },
    M3A: { lat: 43.7533, lng: -79.3297 }, M3B: { lat: 43.7459, lng: -79.3522 },
    M3C: { lat: 43.7259, lng: -79.3409 }, M3H: { lat: 43.7543, lng: -79.4423 },
    M3J: { lat: 43.7679, lng: -79.4873 }, M3K: { lat: 43.7375, lng: -79.4648 },
    M3L: { lat: 43.7390, lng: -79.5069 }, M3M: { lat: 43.7285, lng: -79.4957 },
    M3N: { lat: 43.7615, lng: -79.5209 }, M4A: { lat: 43.7276, lng: -79.3148 },
    M4B: { lat: 43.7063, lng: -79.3099 }, M4C: { lat: 43.6953, lng: -79.3184 },
    M4E: { lat: 43.6764, lng: -79.2930 }, M4G: { lat: 43.7091, lng: -79.3635 },
    M4H: { lat: 43.7054, lng: -79.3494 }, M4J: { lat: 43.6853, lng: -79.3381 },
    M4K: { lat: 43.6796, lng: -79.3522 }, M4L: { lat: 43.6680, lng: -79.3156 },
    M4M: { lat: 43.6595, lng: -79.3409 }, M4N: { lat: 43.7280, lng: -79.3888 },
    M4P: { lat: 43.7128, lng: -79.3902 }, M4R: { lat: 43.7154, lng: -79.4057 },
    M4S: { lat: 43.7043, lng: -79.3888 }, M4T: { lat: 43.6896, lng: -79.3832 },
    M4V: { lat: 43.6864, lng: -79.4000 }, M4W: { lat: 43.6796, lng: -79.3775 },
    M4X: { lat: 43.6679, lng: -79.3677 }, M4Y: { lat: 43.6659, lng: -79.3832 },
    M5A: { lat: 43.6543, lng: -79.3606 }, M5B: { lat: 43.6572, lng: -79.3783 },
    M5C: { lat: 43.6515, lng: -79.3754 }, M5E: { lat: 43.6456, lng: -79.3754 },
    M5G: { lat: 43.6579, lng: -79.3874 }, M5H: { lat: 43.6506, lng: -79.3846 },
    M5J: { lat: 43.6408, lng: -79.3818 }, M5K: { lat: 43.6472, lng: -79.3816 },
    M5L: { lat: 43.6482, lng: -79.3798 }, M5M: { lat: 43.7333, lng: -79.4197 },
    M5N: { lat: 43.7117, lng: -79.4169 }, M5P: { lat: 43.6969, lng: -79.4113 },
    M5R: { lat: 43.6727, lng: -79.4057 }, M5S: { lat: 43.6627, lng: -79.4000 },
    M5T: { lat: 43.6532, lng: -79.4000 }, M5V: { lat: 43.6289, lng: -79.3944 },
    M6A: { lat: 43.7185, lng: -79.4648 }, M6B: { lat: 43.7096, lng: -79.4451 },
    M6C: { lat: 43.6938, lng: -79.4282 }, M6E: { lat: 43.6890, lng: -79.4530 },
    M6G: { lat: 43.6695, lng: -79.4226 }, M6H: { lat: 43.6690, lng: -79.4423 },
    M6J: { lat: 43.6479, lng: -79.4197 }, M6K: { lat: 43.6368, lng: -79.4282 },
    M6L: { lat: 43.7137, lng: -79.4900 }, M6M: { lat: 43.6911, lng: -79.4760 },
    M6N: { lat: 43.6732, lng: -79.4873 }, M6P: { lat: 43.6616, lng: -79.4648 },
    M6R: { lat: 43.6489, lng: -79.4563 }, M6S: { lat: 43.6516, lng: -79.4844 },
    M7A: { lat: 43.6623, lng: -79.3895 }, M8V: { lat: 43.6056, lng: -79.5013 },
    M8W: { lat: 43.6024, lng: -79.5435 }, M8X: { lat: 43.6537, lng: -79.5069 },
    M8Y: { lat: 43.6363, lng: -79.4985 }, M8Z: { lat: 43.6288, lng: -79.5209 },
    M9A: { lat: 43.6679, lng: -79.5322 }, M9B: { lat: 43.6503, lng: -79.5547 },
    M9C: { lat: 43.6435, lng: -79.5772 }, M9L: { lat: 43.7563, lng: -79.5650 },
    M9M: { lat: 43.7248, lng: -79.5322 }, M9N: { lat: 43.7068, lng: -79.5182 },
    M9P: { lat: 43.6963, lng: -79.5322 }, M9R: { lat: 43.6889, lng: -79.5547 },
    M9V: { lat: 43.7394, lng: -79.5884 }, M9W: { lat: 43.7067, lng: -79.5941 }
  };

  const postalRegionFallbacks = {
    L: { lat: 43.6532, lng: -79.6000 },
    K: { lat: 45.4215, lng: -75.6972 },
    N: { lat: 43.2557, lng: -79.8711 },
    P: { lat: 46.4917, lng: -80.9930 },
    T: { lat: 51.0447, lng: -114.0719 },
    V: { lat: 49.2827, lng: -123.1207 },
    Y: { lat: 60.7212, lng: -135.0568 }
  };

  const hashString = (value) => {
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
      hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
  };

  const estimatePostalLocation = (postalCode) => {
    const normalized = String(postalCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const fsa = normalized.slice(0, 3);
    const firstLetter = normalized[0];
    const base = fsaCoords[fsa] || postalRegionFallbacks[firstLetter];
    if (!base) return null;

    const hash = hashString(normalized);
    const angle = (hash % 360) * Math.PI / 180;
    const distance = ((hash % 100) / 100) * 0.009;
    return {
      lat: Number((base.lat + Math.sin(angle) * distance).toFixed(6)),
      lng: Number((base.lng + Math.cos(angle) * distance).toFixed(6))
    };
  };

  const buildPostalClusterMarkers = (dashboardData, idPrefix = 'region') => {
    const sourceRegions = Array.isArray(dashboardData?.regionMapData) && dashboardData.regionMapData.length > 0
      ? dashboardData.regionMapData
      : (dashboardData?.regionData || []).filter(region => region.name !== 'No postal code');

    return sourceRegions.map((region, index) => {
      const storedLocation = region.location;
      const knownRegionLocation = regionCoords[region.name];
      const estimatedLocation = !storedLocation && !knownRegionLocation ? estimatePostalLocation(region.name) : null;
      const knownLocation = storedLocation || knownRegionLocation || estimatedLocation;
      const precision = region.sourcePrecision || region.precision || region.locationPrecision
        || (storedLocation || knownRegionLocation ? 'postal_code_geocode' : (estimatedLocation ? 'estimate' : 'missing'));

      return {
        id: `${idPrefix}-${index}-${String(region.name || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`,
        name: region.name,
        location: knownLocation,
        geocodeQuery: knownLocation ? null : (region.geocodeQuery || `${region.name}, Canada`),
        value: region.value,
        sourcePrecision: precision,
        type: region.type || 'postal_cluster',
        color: '#3b82f6',
        icon: Users,
        customInfo: region.customInfo || `${region.value} members in postal code ${region.name}`
      };
    });
  };

  const buildMemberRadiusLocations = (dashboardData, idPrefix = 'member') => {
    const members = Array.isArray(dashboardData?.memberMapData) ? dashboardData.memberMapData : [];
    return members.map((member, index) => {
      const postalCode = member.postalCode || member.postal_code || '';
      const storedLocation = member.location;
      const estimatedLocation = !storedLocation ? estimatePostalLocation(postalCode) : null;
      const knownLocation = storedLocation || estimatedLocation;
      if (!knownLocation) return null;

      return {
        id: member.id || `${idPrefix}-${index}-${String(postalCode || 'member').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`,
        name: member.memberName || member.fullName || member.name || 'Unnamed member',
        memberName: member.memberName || member.fullName || member.name || 'Unnamed member',
        firstName: member.firstName,
        lastName: member.lastName,
        postalCode,
        status: member.status,
        location: knownLocation,
        geocodeQuery: knownLocation ? null : (member.geocodeQuery || `${postalCode}, Canada`),
        value: 1,
        sourcePrecision: storedLocation ? 'postal_code_geocode' : 'estimate',
        type: 'member'
      };
    }).filter(Boolean);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, statsRes, analyticsRes, linksRes, mapDataRes] = await Promise.all([
      fetch(`${API_URL}/api/campaign-intelligence/campaigns`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/stats`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/analytics/overview?days=30`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/tracking-links`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/map-data`, { headers: getAuthHeaders() })]
      );

      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (linksRes.ok) setTrackingLinks(await linksRes.json());
      
      if (mapDataRes.ok) {
        setMapMarkers(buildPostalClusterMarkers(defaultManualData, 'global'));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignDetails = async (campaignId) => {
    setLoading(true);
    try {
      const [analyticsRes, linksRes, mapDataRes] = await Promise.all([
      fetch(`${API_URL}/api/campaign-intelligence/campaigns/${campaignId}`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/tracking-links?campaign_id=${campaignId}`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/campaign-intelligence/map-data?campaign_id=${campaignId}`, { headers: getAuthHeaders() })]
      );

      if (analyticsRes.ok) {
        const campaignData = await analyticsRes.json();
        setAnalytics(campaignData.analytics || {});
        let mergedData = defaultManualData;
        if (campaignData.manual_data) {
          mergedData = { ...defaultManualData, ...campaignData.manual_data };
          setManualData(mergedData);
        } else {
          setManualData(defaultManualData);
        }

        setMapMarkers(buildPostalClusterMarkers(mergedData, 'region'));
      }
      if (linksRes.ok) setTrackingLinks(await linksRes.json());
    } catch (error) {
      toast.error('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCampaign = (campaign) => {
    setSelectedCampaignView(campaign);
    setActiveTab('analytics');
    fetchCampaignDetails(campaign.id);
  };

  useEffect(() => {
    if (!requestedTab || requestedCampaignId) return;
    setActiveTab(requestedTab);
  }, [requestedTab, requestedCampaignId]);

  useEffect(() => {
    if (!requestedCampaignId || campaigns.length === 0) return;
    if (String(selectedCampaignView?.id || '') === String(requestedCampaignId)) {
      if (requestedTab) setActiveTab(requestedTab);
      return;
    }

    const campaign = campaigns.find((item) => String(item.id) === String(requestedCampaignId));
    if (!campaign) return;

    setSelectedCampaignView(campaign);
    setActiveTab(requestedTab || 'analytics');
    fetchCampaignDetails(campaign.id);
  }, [campaigns, requestedCampaignId, requestedTab, selectedCampaignView?.id]);

  const handleBackToGlobal = () => {
    setSelectedCampaignView(null);
    setActiveTab('campaigns');
    fetchData();
  };

  const isBrazilianRewardsCampaign = (campaign) => {
    const identity = `${campaign?.name || ''} ${campaign?.description || ''}`.toLowerCase();
    return identity.includes('brazilians rewards') || identity.includes('brazilian rewards');
  };

  const handleRefreshClick = async () => {
    if (!selectedCampaignView) {
      fetchData();
      return;
    }

    if (!isBrazilianRewardsCampaign(selectedCampaignView)) {
      fetchCampaignDetails(selectedCampaignView.id);
      return;
    }

    setIsSourceRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/campaign-intelligence/campaigns/${selectedCampaignView.id}/refresh-source`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          source: 'franz_br',
          mode: 'safe_merge',
          confirm_preserve_enrichment: true
        })
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'BR source refresh failed');
      }

      const preservation = result.preservation || {};
      const counts = result.import_counts || {};
      toast.success('BR refreshed from Franz/BR source', {
        description: `Batch ${result.batch_id}; partners ${preservation.partners_before || 0}->${preservation.partners_after || 0}; coordinates ${preservation.coordinates_before || 0}->${preservation.coordinates_after || 0}; members ${counts.persons || 0}.`
      });
      await fetchCampaignDetails(selectedCampaignView.id);
    } catch (error) {
      toast.error('BR refresh did not update data', {
        description: error.message
      });
    } finally {
      setIsSourceRefreshing(false);
    }
  };

  const createCampaign = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/campaigns`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newCampaign,
          budget: newCampaign.budget ? parseFloat(newCampaign.budget) : null
        })
      });

      if (!res.ok) throw new Error('Failed to create campaign');

      toast.success('Campaign created successfully');
      setShowNewCampaignDialog(false);
      setNewCampaign({
        name: '',
        description: '',
        target_modules: [],
        target_industries: [],
        tracking_domain: '',
        enable_website_tracking: true,
        enable_email_tracking: true,
        enable_social_tracking: true,
        start_date: '',
        end_date: '',
        budget: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const createTrackingLink = async () => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/tracking-links`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newLink,
          campaign_id: parseInt(newLink.campaign_id)
        })
      });

      if (!res.ok) throw new Error('Failed to create tracking link');

      const data = await res.json();
      toast.success('Tracking link created');
      setShowNewLinkDialog(false);
      setNewLink({
        campaign_id: '',
        destination_url: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getTrackingScript = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/tracking-script/${campaignId}`, {
        headers: getAuthHeaders()
      });

      if (!res.ok) throw new Error('Failed to get tracking script');

      const data = await res.json();
      setTrackingScript(data.script);
      setShowTrackingScriptDialog(true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      const res = await fetch(`${API_URL}/api/campaign-intelligence/campaigns/${campaign.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (!res.ok) throw new Error('Failed to update campaign');

      toast.success(`Campaign ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading && campaigns.length === 0 && !selectedCampaignView) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50/50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          <p className="text-sm font-medium text-muted-foreground">{t("loadingCampaign", "Loading campaign intelligence...")}</p>
        </div>
      </div>
    );
  }

  const postalClusterCount = Array.isArray(manualData.regionMapData) && manualData.regionMapData.length > 0
    ? manualData.regionMapData.length
    : (manualData.regionData || []).filter(region => region.name !== 'No postal code').length;
  const mappableMembers = manualData.dadosCompletos?.comPostalCode || 0;
  const missingPostalMembers = Math.max((manualData.kpis?.total || 0) - mappableMembers, 0);
  const individualMemberRadiusLocations = buildMemberRadiusLocations(manualData, 'radius-member-analysis');
  const aggregateMemberRadiusLocations = buildPostalClusterMarkers(manualData, 'radius-analysis')
    .filter(marker => marker.type === 'postal_cluster')
    .map(marker => ({
      id: marker.id,
      name: marker.name,
      location: marker.location,
      value: Number(marker.value) || 0,
      sourcePrecision: marker.sourcePrecision || 'estimate',
      type: 'postal_cluster'
    }));
  const allMemberRadiusLocations = individualMemberRadiusLocations.length > 0
    ? individualMemberRadiusLocations
    : aggregateMemberRadiusLocations;
  const memberRadiusData = {
    locations: allMemberRadiusLocations,
    totalMembers: manualData.kpis?.total || 0,
    mappableMembers: individualMemberRadiusLocations.length > 0 ? individualMemberRadiusLocations.length : mappableMembers,
    postalClusterCount,
    excludedMissingLocation: individualMemberRadiusLocations.length > 0
      ? Math.max((manualData.kpis?.total || 0) - individualMemberRadiusLocations.length, 0)
      : missingPostalMembers
  };
  const normalizedMapSearchQuery = mapSearchQuery.trim().toLowerCase();
  const matchesMapSearch = (...values) => {
    if (!normalizedMapSearchQuery) return true;
    return values
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(normalizedMapSearchQuery));
  };
  const visibleMemberMarkers = selectedMapLayers.includes('members')
    ? mapMarkers.filter(marker => matchesMapSearch(marker.name, marker.customInfo, marker.geocodeQuery))
    : [];
  const visiblePartnerClients = selectedMapLayers.includes('clients')
    ? (manualData.clients || []).filter(client => matchesMapSearch(client.name, client.address, client.email, client.website, client.category, client.status))
    : [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        {selectedCampaignView ? (
          <div className="flex flex-col gap-2">
            <Button variant="ghost" className="w-fit -ml-2 text-muted-foreground hover:text-foreground" onClick={handleBackToGlobal}>
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("backToCampaigns", "Back to All Campaigns")}
            </Button>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{selectedCampaignView.name}</h1>
              <Badge className={statusColors[selectedCampaignView.status]}>{selectedCampaignView.status}</Badge>
            </div>
            <p className="text-muted-foreground">{selectedCampaignView.description || t("campaignDetails", "Campaign Details & Analytics")}</p>
          </div>
        ) : (
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("campaignIntelligence", "Campaign Intelligence")}</h1>
            <p className="text-muted-foreground">{t("trackAnalyzeAnd", "Track, analyze, and optimize your marketing campaigns")}</p>
          </div>
        )}
        
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleRefreshClick} disabled={loading || isSourceRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${(loading || isSourceRefreshing) ? 'animate-spin' : ''}`} />
            {t("refresh", "Refresh")}
          </Button>
          {!selectedCampaignView && (
            <>
              <Button variant="outline" onClick={() => setShowNewLinkDialog(true)}>
                <Link2 className="h-4 w-4 mr-2" />{t("newLink", "New Link")}
              </Button>
              <Button variant="outline" onClick={() => setShowTrackingScriptDialog(true)}>
                <Code className="h-4 w-4 mr-2" />{t("trackingScript", "Tracking Script")}
              </Button>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={() => setShowNewCampaignDialog(true)}>
                <Plus className="h-4 w-4" />{t("newCampaign", "New Campaign")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards (Only Global) */}
      {!selectedCampaignView && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="card-elevated border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("activeCampaigns", "Active Campaigns")}</p>
                  <p className="text-2xl font-bold">{stats.active_campaigns || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-elevated border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal-500/10">
                  <Users className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("totalVisitors", "Total Visitors")}</p>
                  <p className="text-2xl font-bold">{stats.total_visitors || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-elevated border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <MousePointer className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("totalClicks", "Total Clicks")}</p>
                  <p className="text-2xl font-bold">{stats.total_clicks || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-elevated border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Zap className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("totalEvents", "Total Events")}</p>
                  <p className="text-2xl font-bold">{stats.total_events || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${selectedCampaignView ? 'grid-cols-4 lg:w-[600px]' : 'grid-cols-5 lg:w-[750px]'}`}>
          {!selectedCampaignView && (
            <TabsTrigger value="campaigns" className="gap-2">
              <Target className="h-4 w-4" />{t("campaigns", "Campaigns")}
            </TabsTrigger>
          )}
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />{t("analytics", "Analytics")}

          </TabsTrigger>
          <TabsTrigger value="links" className="gap-2">
            <Link2 className="h-4 w-4" />{t("links", "Links")}

          </TabsTrigger>
          <TabsTrigger value="tracking" className="gap-2">
            <Code className="h-4 w-4" />{t("tracking", "Tracking")}

          </TabsTrigger>
          <TabsTrigger value="map" className="gap-2">
            <MapIcon className="h-4 w-4" />{t("map", "Map")}
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        {!selectedCampaignView && (
          <TabsContent value="campaigns" className="space-y-4">
            {campaigns.length === 0 ?
            <Card className="card-elevated">
                <CardContent className="py-12 text-center">
                  <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noMarketingCampaigns", "No Marketing Campaigns")}</h3>
                  <p className="text-muted-foreground mb-4">{t("createYourFirst", "Create your first campaign to start tracking")}</p>
                  <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowNewCampaignDialog(true)}>
                  
                    <Plus className="mr-2 h-4 w-4" />{t("createCampaign", "Create Campaign")}
                </Button>
                </CardContent>
              </Card> :

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {campaigns.map((campaign) =>
              <Card key={campaign.id} className="card-elevated hover:shadow-lg transition-all border border-border cursor-pointer" onClick={() => handleViewCampaign(campaign)}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">{campaign.name}</CardTitle>
                          <CardDescription className="mt-1 line-clamp-2">
                            {campaign.description || 'No description'}
                          </CardDescription>
                        </div>
                        <Badge className={statusColors[campaign.status]}>
                          {campaign.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Tracking Config */}
                      <div className="flex gap-2">
                        {campaign.enable_website_tracking &&
                    <Badge variant="outline" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />{t("website", "Website")}
                    </Badge>
                    }
                        {campaign.enable_email_tracking &&
                    <Badge variant="outline" className="text-xs">
                            <Mail className="h-3 w-3 mr-1" />{t("email", "Email")}
                    </Badge>
                    }
                        {campaign.enable_social_tracking &&
                    <Badge variant="outline" className="text-xs">
                            <Share2 className="h-3 w-3 mr-1" />{t("social", "Social")}
                    </Badge>
                    }
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-4 gap-2 text-center pt-2 border-t">
                        <div>
                          <p className="text-lg font-bold">{campaign.visitor_count || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("visitors", "Visitors")}</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-orange-600">{campaign.total_clicks || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("clicks", "Clicks")}</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-blue-600">{campaign.asset_count || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("assets", "Assets")}</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-teal-600">{campaign.link_count || 0}</p>
                          <p className="text-xs text-muted-foreground">{t("links", "Links")}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-between items-center pt-2">
                        <Button
                          size="sm"
                          className="w-full bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40"
                          onClick={(e) => { e.stopPropagation(); handleViewCampaign(campaign); }}>
                          View Campaign Details <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
              )}
              </div>
            }
          </TabsContent>
        )}

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Header & Filters */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-[24px] font-extrabold tracking-tight text-[#1e1e2d] leading-none">Campaign Dashboard</h2>
              <p className="text-gray-500 text-sm mt-1">Detailed performance and metrics for {selectedCampaignView?.name}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleOpenEdit} className="gap-2 bg-blue-50">
                <Settings2 className="h-4 w-4" /> Edit Campaign Data
              </Button>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Budget: <strong>${manualData.finances?.budget || 0}</strong></span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Costs: <strong>${manualData.finances?.costs || 0}</strong></span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm text-sm text-gray-600">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                <span>Últimos 7 dias: <strong>{manualData.kpis.novos7dias} novos</strong></span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span>Este mês: <strong>{manualData.kpis.novosEsteMes} novos</strong></span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-red-200 rounded-lg px-4 py-2 shadow-sm text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span>Bounced: <strong>{manualData.kpis.bounced}</strong></span>
              </div>
            </div>
          </div>

          {/* Row 1: Main KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="bg-blue-50 text-blue-500 p-3 rounded-xl"><Users className="h-6 w-6" /></div>
              <div>
                <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.kpis.total.toLocaleString()}</div>
                <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Total Members</div>
              </div>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="bg-green-50 text-green-500 p-3 rounded-xl"><CheckCircle2 className="h-6 w-6" /></div>
              <div>
                <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.kpis.ativos.toLocaleString()}</div>
                <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Ativos</div>
              </div>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="bg-purple-50 text-purple-500 p-3 rounded-xl"><CheckCircle2 className="h-6 w-6" /></div>
              <div>
                <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.kpis.verificados.toLocaleString()}</div>
                <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Verificados</div>
              </div>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="bg-red-50 text-red-500 p-3 rounded-xl"><XOctagon className="h-6 w-6" /></div>
              <div>
                <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.kpis.bounced.toLocaleString()}</div>
                <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Bounced</div>
              </div>
            </div>
          </div>

          {/* Row 2: Engajamento & B2B/B2C */}
          <div className="mb-8">
            <h2 className="text-sm font-bold text-[#1e1e2d] flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4" /> Engajamento e Segmentação
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-yellow-50 text-yellow-500 p-3 rounded-xl"><Ticket className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.engajamento.comCupons.toLocaleString()}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Com Cupons</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-teal-50 text-teal-500 p-3 rounded-xl"><Calendar className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.engajamento.emEventos.toLocaleString()}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Em Eventos</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-indigo-50 text-indigo-500 p-3 rounded-xl"><Building2 className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.finances?.b2b || 0}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">B2B Tracking</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-blue-50 text-blue-500 p-3 rounded-xl"><Users className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.finances?.b2c || 0}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">B2C Tracking</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-gray-100 text-gray-400 p-3 rounded-xl"><Ghost className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.engajamento.semEngajamento.toLocaleString()}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Sem Engajamento</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-pink-50 text-pink-500 p-3 rounded-xl"><Building2 className="h-6 w-6" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.engajamento.multiEmpresa || 0}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Multi-empresa</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Linha de Cupons */}
          <div className="mb-8">
            <h2 className="text-sm font-bold text-[#1e1e2d] flex items-center gap-2 mb-3">
              <Ticket className="h-4 w-4" /> Linha de Cupons
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-teal-50 text-teal-500 p-3 rounded-xl"><Users className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.pessoasUsaram}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Pessoas que Usaram</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-blue-50 text-blue-500 p-3 rounded-xl"><Ticket className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.claims}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Claims</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-orange-50 text-orange-500 p-3 rounded-xl"><CheckCircle2 className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.resgatados}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Resgatados</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-red-50 text-red-500 p-3 rounded-xl"><XOctagon className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.claimSemResgate || 0}</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Claim Sem Resgate</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-purple-50 text-purple-500 p-3 rounded-xl"><Calendar className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.tempoMedio || 0} horas</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Tempo Médio Claim-Resgate</div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-start gap-4">
                <div className="bg-indigo-50 text-indigo-500 p-3 rounded-xl"><TrendingUp className="h-5 w-5" /></div>
                <div>
                  <div className="text-[24px] font-bold text-[#1e1e2d] leading-none">{manualData.linhaCupons.taxaResgate || 0}%</div>
                  <div className="text-[11px] font-bold text-gray-400 tracking-wider mt-1 uppercase">Taxa de Resgate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Lists and Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                <h3 className="text-[15px] font-bold text-[#1e1e2d] mb-4">Daily Activity (Last 30 Days)</h3>
                <div className="h-[250px]">
                  {analytics.daily_breakdown && analytics.daily_breakdown.length > 0 ?
                  <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.daily_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} className="text-xs" />
                        <YAxis className="text-xs" />
                        <Tooltip />
                        <Area type="monotone" dataKey="sessions" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Sessions" />
                        <Area type="monotone" dataKey="visitors" stackId="2" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.3} name="Visitors" />
                      </AreaChart>
                    </ResponsiveContainer> :
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>{t("noDataAvailable", "No data available yet")}</p>
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                <h3 className="text-[15px] font-bold text-[#1e1e2d] mb-4">Analytics Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-blue-500/10">
                      <p className="text-2xl font-bold text-blue-600">{analytics.summary?.total_visitors || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Visitors</p>
                    </div>
                    <div className="p-4 rounded-lg bg-teal-500/10">
                      <p className="text-2xl font-bold text-teal-600">{analytics.summary?.total_sessions || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Sessions</p>
                    </div>
                    <div className="p-4 rounded-lg bg-orange-500/10">
                      <p className="text-2xl font-bold text-orange-600">{analytics.summary?.page_views || 0}</p>
                      <p className="text-xs text-muted-foreground">Page Views</p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-500/10">
                      <p className="text-2xl font-bold text-purple-600">{analytics.summary?.clicks || 0}</p>
                      <p className="text-xs text-muted-foreground">Clicks</p>
                    </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                <h3 className="text-[15px] font-bold text-[#1e1e2d] mb-4">Members por Região</h3>
                <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                  {manualData.regionData.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm border-b pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-700">{item.name}</span>
                      </div>
                      <span className="font-bold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
                <h3 className="text-[15px] font-bold text-[#1e1e2d] mb-4">Empresas com mais Members</h3>
                <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                  {manualData.companyData.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm border-b pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-700">{item.name}</span>
                      </div>
                      <span className="font-bold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tracking Links Tab */}
        <TabsContent value="links" className="space-y-4">
          <Card className="card-elevated">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("campaign", "Campaign")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("trackingUrl", "Tracking URL")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("utmSource", "UTM Source")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("clicks", "Clicks")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("unique", "Unique")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingLinks.map((link) =>
                    <tr key={link.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-3 px-4">
                          <p className="font-medium">{link.campaign_name}</p>
                          <p className="text-xs text-muted-foreground">{link.asset_name || 'No asset'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {API_URL}{t("apicampaignintelligence", "/api/campaign-intelligence")}{link.tracking_url}
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{link.utm_source || 'direct'}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-lg font-bold text-orange-600">{link.click_count}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-lg font-bold text-blue-600">{link.unique_clicks}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => copyToClipboard(`${API_URL}/api/campaign-intelligence${link.tracking_url}`)}>
                            
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => window.open(link.destination_url, '_blank')}>
                            
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {trackingLinks.length === 0 &&
              <div className="py-12 text-center">
                  <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noTrackingLinks", "No Tracking Links")}</h3>
                  <p className="text-muted-foreground mb-4">{t("createTrackingLinks", "Create tracking links to measure campaign performance")}</p>
                  <Button
                  variant="outline"
                  onClick={() => setShowNewLinkDialog(true)}>
                  
                    <Plus className="mr-2 h-4 w-4" />{t("createTrackingLink", "Create Tracking Link")}

                </Button>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tracking Setup Tab */}
        <TabsContent value="tracking" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("websiteTrackingSetup", "Website Tracking Setup")}</CardTitle>
              <CardDescription>{t("addTheTracking", "Add the tracking script to your website to collect visitor analytics")}

              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                {campaigns.filter((c) => c.status === 'active').map((campaign) =>
                <div
                  key={campaign.id}
                  className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => getTrackingScript(campaign.id)}>
                  
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{campaign.name}</h4>
                        <p className="text-sm text-muted-foreground">{t("clickToGet", "Click to get tracking script")}</p>
                      </div>
                      <Code className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                )}
              </div>

              {campaigns.filter((c) => c.status === 'active').length === 0 &&
              <div className="py-12 text-center">
                  <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{t("noActiveCampaigns", "No Active Campaigns")}</h3>
                  <p className="text-muted-foreground">{t("activateACampaign", "Activate a campaign to get tracking scripts")}</p>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Map Tab */}
        <TabsContent value="map" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-4">
              <div className="flex items-center gap-2">
                <CardTitle>{t("campaignMap", "Campaign Map")}</CardTitle>
                <UITooltipProvider delayDuration={150}>
                  <UITooltip>
                    <UITooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        aria-label={t("mapCoverageDetails", "Map coverage details")}
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </UITooltipTrigger>
                    <UITooltipContent side="right" align="start" className="space-y-1 text-xs leading-5">
                      <div>{postalClusterCount.toLocaleString()} postal clusters</div>
                      <div>{mappableMembers.toLocaleString()} mappable members</div>
                      <div>{missingPostalMembers.toLocaleString()} missing postal code</div>
                    </UITooltipContent>
                  </UITooltip>
                </UITooltipProvider>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
                <Input 
                  placeholder={t("searchMap", "Search postal code, partner...")}
                  value={mapSearchQuery}
                  onChange={(e) => setMapSearchQuery(e.target.value)}
                  className="w-56 h-8 text-xs mr-2"
                />
                {mapLayerOptions.map(layer => (
                  <Badge 
                    key={layer.id}
                    variant={selectedMapLayers.includes(layer.id) ? "default" : "outline"}
                    className="cursor-pointer text-xs py-1"
                    style={{ 
                      backgroundColor: selectedMapLayers.includes(layer.id) ? layer.color : 'transparent',
                      color: selectedMapLayers.includes(layer.id) ? '#fff' : layer.color,
                      borderColor: layer.color
                    }}
                    onClick={() => {
                      setSelectedMapLayers(prev => 
                        prev.includes(layer.id) 
                          ? prev.filter(l => l !== layer.id)
                          : [...prev, layer.id]
                      )
                    }}
                  >
                    {layer.label}
                  </Badge>
                ))}
                <Button variant="outline" onClick={() => setShowApiKeyDialog(true)} className="gap-2 ml-2 shrink-0">
                   <Key className="h-4 w-4" /> {t("editApiKey", "Edit API Key")}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[600px] relative">
                {!googleApiKey ? (
                   <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/80 dark:bg-gray-900/80 z-10">
                     <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-center max-w-sm">
                       <Key className="h-10 w-10 text-amber-500 mx-auto mb-4" />
                       <h3 className="text-lg font-semibold mb-2">{t("apiRequired", "API Key Required")}</h3>
                       <p className="text-sm text-gray-500 mb-6">Você precisa configurar a chave da API do Google Maps para visualizar as campanhas no mapa.</p>
                       <Button onClick={() => setShowApiKeyDialog(true)}>Configurar API Key</Button>
                     </div>
                   </div>
                ) : (
                  <GoogleMap
                    apiKey={googleApiKey}
                    markers={visibleMemberMarkers}
                    selectedMarker={selectedMarker}
                    onMarkerSelect={setSelectedMarker}
                    center={{ lat: 43.6532, lng: -79.3832 }}
                    zoom={11}
                    placesTypes={selectedMapLayers}
                    clientsData={visiblePartnerClients}
                    memberRadiusData={memberRadiusData}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* API Key Dialog */}
      <ApiKeyDialog
        open={showApiKeyDialog}
        onOpenChange={setShowApiKeyDialog}
        onSave={handleSaveApiKey}
        currentKey={googleApiKey}
      />

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaignDialog} onOpenChange={setShowNewCampaignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createMarketingCampaign", "Create Marketing Campaign")}</DialogTitle>
            <DialogDescription>{t("setUpTracking", "Set up tracking for your marketing initiatives")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">{t("campaignName", "Campaign Name *")}</Label>
              <Input
                id="name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="e.g., Q1 Product Launch" />
              
            </div>

            <div>
              <Label htmlFor="description">{t("description", "Description")}</Label>
              <Input
                id="description"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Campaign goals and target audience" />
              
            </div>

            <div>
              <Label>{t("trackingDomainOptional", "Tracking Domain (optional)")}</Label>
              <Input
                value={newCampaign.tracking_domain}
                onChange={(e) => setNewCampaign({ ...newCampaign, tracking_domain: e.target.value })}
                placeholder="e.g., track.yourdomain.com" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("startDate", "Start Date")}</Label>
                <Input
                  type="date"
                  value={newCampaign.start_date}
                  onChange={(e) => setNewCampaign({ ...newCampaign, start_date: e.target.value })} />
                
              </div>
              <div>
                <Label>{t("endDate", "End Date")}</Label>
                <Input
                  type="date"
                  value={newCampaign.end_date}
                  onChange={(e) => setNewCampaign({ ...newCampaign, end_date: e.target.value })} />
                
              </div>
            </div>

            <div>
              <Label>{t("budgetOptional", "Budget (optional)")}</Label>
              <Input
                type="number"
                value={newCampaign.budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: e.target.value })}
                placeholder="Campaign budget" />
              
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCampaignDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={createCampaign}
              disabled={!newCampaign.name}>{t("createCampaign", "Create Campaign")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Tracking Link Dialog */}
      <Dialog open={showNewLinkDialog} onOpenChange={setShowNewLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("createTrackingLink", "Create Tracking Link")}</DialogTitle>
            <DialogDescription>{t("generateATrackable", "Generate a trackable link for your campaign")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>{t("campaign", "Campaign *")}</Label>
              <Select
                value={newLink.campaign_id}
                onValueChange={(value) => setNewLink({ ...newLink, campaign_id: value })}>
                
                <SelectTrigger>
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) =>
                  <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("destinationUrl", "Destination URL *")}</Label>
              <Input
                value={newLink.destination_url}
                onChange={(e) => setNewLink({ ...newLink, destination_url: e.target.value })}
                placeholder="https://yoursite.com/landing-page" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("utmSource", "UTM Source")}</Label>
                <Input
                  value={newLink.utm_source}
                  onChange={(e) => setNewLink({ ...newLink, utm_source: e.target.value })}
                  placeholder="e.g., facebook" />
                
              </div>
              <div>
                <Label>{t("utmMedium", "UTM Medium")}</Label>
                <Input
                  value={newLink.utm_medium}
                  onChange={(e) => setNewLink({ ...newLink, utm_medium: e.target.value })}
                  placeholder="e.g., social" />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("utmCampaign", "UTM Campaign")}</Label>
                <Input
                  value={newLink.utm_campaign}
                  onChange={(e) => setNewLink({ ...newLink, utm_campaign: e.target.value })}
                  placeholder="e.g., spring_sale" />
                
              </div>
              <div>
                <Label>{t("utmContent", "UTM Content")}</Label>
                <Input
                  value={newLink.utm_content}
                  onChange={(e) => setNewLink({ ...newLink, utm_content: e.target.value })}
                  placeholder="e.g., banner_1" />
                
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLinkDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={createTrackingLink}
              disabled={!newLink.campaign_id || !newLink.destination_url}>{t("createLink", "Create Link")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tracking Script Dialog */}
      <Dialog open={showTrackingScriptDialog} onOpenChange={setShowTrackingScriptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("trackingScript", "Tracking Script")}</DialogTitle>
            <DialogDescription>{t("addThisScript", "Add this script to your website to enable analytics tracking")}

            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="relative">
              <pre className="p-4 rounded-lg bg-muted overflow-x-auto text-xs max-h-[400px]">
                <code>{trackingScript}</code>
              </pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(trackingScript)}>
                
                <Copy className="h-4 w-4 mr-1" />{t("copy", "Copy")}

              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrackingScriptDialog(false)}>{t("close", "Close")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Entry Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-500" /> Override Campaign Dashboard Data
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-gray-50">
            {editData && (
              <>
                {/* Finances Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Costs & Budget</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Budget ($)</Label>
                      <Input type="number" value={editData.finances?.budget || 0} onChange={(e) => setEditData({...editData, finances: {...editData.finances, budget: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Costs ($)</Label>
                      <Input type="number" value={editData.finances?.costs || 0} onChange={(e) => setEditData({...editData, finances: {...editData.finances, costs: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>B2B Tracking</Label>
                      <Input type="number" value={editData.finances?.b2b || 0} onChange={(e) => setEditData({...editData, finances: {...editData.finances, b2b: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>B2C Tracking</Label>
                      <Input type="number" value={editData.finances?.b2c || 0} onChange={(e) => setEditData({...editData, finances: {...editData.finances, b2c: Number(e.target.value)}})} />
                    </div>
                  </div>
                </div>

                {/* Main KPIs Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Main KPIs</h3>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="space-y-2">
                      <Label>Total Members</Label>
                      <Input type="number" value={editData.kpis.total} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, total: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Ativos</Label>
                      <Input type="number" value={editData.kpis.ativos} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, ativos: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Verificados</Label>
                      <Input type="number" value={editData.kpis.verificados} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, verificados: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Bounced</Label>
                      <Input type="number" value={editData.kpis.bounced} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, bounced: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Últimos 7 dias</Label>
                      <Input type="number" value={editData.kpis.novos7dias || 0} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, novos7dias: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Este mês</Label>
                      <Input type="number" value={editData.kpis.novosEsteMes || 0} onChange={(e) => setEditData({...editData, kpis: {...editData.kpis, novosEsteMes: Number(e.target.value)}})} />
                    </div>
                  </div>
                </div>

                {/* Engajamento Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Engajamento</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Com Cupons</Label>
                      <Input type="number" value={editData.engajamento.comCupons} onChange={(e) => setEditData({...editData, engajamento: {...editData.engajamento, comCupons: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Em Eventos</Label>
                      <Input type="number" value={editData.engajamento.emEventos} onChange={(e) => setEditData({...editData, engajamento: {...editData.engajamento, emEventos: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Sem Engajamento</Label>
                      <Input type="number" value={editData.engajamento.semEngajamento} onChange={(e) => setEditData({...editData, engajamento: {...editData.engajamento, semEngajamento: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Multi-empresa</Label>
                      <Input type="number" value={editData.engajamento.multiEmpresa || 0} onChange={(e) => setEditData({...editData, engajamento: {...editData.engajamento, multiEmpresa: Number(e.target.value)}})} />
                    </div>
                  </div>
                </div>

                {/* Linha de Cupons Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Linha de Cupons</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Pessoas que Usaram</Label>
                      <Input type="number" value={editData.linhaCupons.pessoasUsaram} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, pessoasUsaram: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Claims</Label>
                      <Input type="number" value={editData.linhaCupons.claims} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, claims: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Resgatados</Label>
                      <Input type="number" value={editData.linhaCupons.resgatados} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, resgatados: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Claim sem resgate</Label>
                      <Input type="number" value={editData.linhaCupons.claimSemResgate || 0} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, claimSemResgate: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tempo Médio (horas)</Label>
                      <Input type="number" step="0.1" value={editData.linhaCupons.tempoMedio || 0} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, tempoMedio: Number(e.target.value)}})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Taxa de Resgate (%)</Label>
                      <Input type="number" step="0.1" value={editData.linhaCupons.taxaResgate || 0} onChange={(e) => setEditData({...editData, linhaCupons: {...editData.linhaCupons, taxaResgate: Number(e.target.value)}})} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="p-6 border-t bg-white flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveData} disabled={isSaving} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Campaign Data
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignIntelligence;
